<template>
	<AppModal
		ref="modal"
		:title="$t(modalTitle)"
		titleIcon="/images/icons/ic-warning-circle.svg"
		aria-labelledby="audienceBulkAddToProductModal"
		:overflow="true"
		:loading="loading"
		action="Grant Product Access"
		@cta="handleCta"
		:disableAction="!confirm"
		secondary-action="Cancel"
		@secondary-cta="hide"
	>
		<Transition name="fade" mode="out-in">
			<!-- Product Selection Promt -->
			<div v-if="isCurrentPromptProductSelection && !loading" key="select-product">
				<form id="audience_bulk_tagging_form">
					<div class="overflow-visible">
						<AppDropdown
							:border="true"
							class="product-dropdown"
							:value="selectedProduct"
							:options="availableProducts"
							:searchable="true"
							placeholder="Select a product"
							image-width="23px"
							:right-align="false"
							display-label="name"
							label="name"
							:AppDropdown="true"
							:small="false"
							:rounded-icons="true"
							@select="selectProduct"
						/>
						<p class="para-3 text-danger mb-2" v-show="formErrors && formErrors.productSelectionErrors">
							{{ formErrors.productSelectionErrors }}
						</p>
					</div>
				</form>
			</div>

			<!-- Confirmation Prompt -->
			<div v-else-if="!isCurrentPromptProductSelection && !loading" key="confirm">
				<div class="confirmation-card">
					<div class="confirmation-count">{{ $commonHelper.formatNumber(tableTotalCustomers, { decimals: 0 }) }}</div>
					<div class="confirmation-label">Recipients</div>
				</div>
				<div class="w-100 mt-3 highlight-card">
					<AppCheckbox v-model="confirm">
						Are you sure you want to grant product access to
						<span class="customer-count">{{ tableTotalCustomers }} {{ customerText }}</span
						>?
					</AppCheckbox>
				</div>
			</div>

			<!-- Loading State -->
			<div v-else key="loading">
				<div class="importing-animation">
					<InlineSvg src="/images/icons/ic-box.svg" />
					<InlineSvg src="/images/icons/ic-box.svg" />
					<InlineSvg src="/images/icons/ic-box.svg" />
				</div>
				<AppProgress :min="0" :max="totalCustomers" :value="processedCount" />
			</div>
		</Transition>
	</AppModal>
</template>
<script>
	import { mapGetters, mapActions } from 'vuex'

	export default {
		props: {
			tableTotalCustomers: {
				type: Number,
				default: 0,
			},
			filterArray: {
				type: Array,
				default: () => [],
			},
		},
		mounted() {
			this.fetchProductTagList()
		},
		data() {
			return {
				formErrors: {},
				loading: false,
				numberOfRowsToProcess: 100,
				processedCount: 0,
				userFanIds: [],
				selectedProduct: null,
				availableProducts: [],
				confirm: false,
				currentPrompt: 'selection',
			}
		},
		created() {
			this.resetForm()
		},
		computed: {
			...mapGetters('Auth', ['user']),
			...mapGetters('Stores', ['getStore', 'getFunnels', 'getStoreLoading']),
			isCurrentPromptProductSelection() {
				return this.currentPrompt === 'selection'
			},
			modalTitle() {
				let title = this.isCurrentPromptProductSelection
					? `Grant Product Access to ${this.tableTotalCustomers} ${this.customerText}`
					: 'Confirming!'
				if (this.loading) {
					title = 'Processing...'
				}

				return title
			},
			modalSubheading() {
				return 'Grant customers free access to a product'
			},
			totalCustomers() {
				return this.userFanIds.length
			},
			customerText() {
				return `customer${this.tableTotalCustomers > 1 ? 's' : ''}`
			},
		},
		methods: {
			...mapActions('Auth', ['fetchUser']),
			show() {
				this.resetForm()
				$(this.$refs.modal.$el).modal('show')
			},
			hide() {
				$(this.$refs.modal.$el).modal('hide')
				this.$emit('close')
				setTimeout(this.resetForm, 250)
			},
			selectProduct(product) {
				this.selectedProduct = product
				this.confirm = !!product
			},
			fetchProductTagList() {
				// Only available for course, community & downloads
				const communityProducts = this.$commonHelper.buildProductList(
					this.getStore,
					this.getFunnels,
					this.$constants.PRODUCT_TYPES.COMMUNITY,
					false,
					true,
					this.user,
					2
				)
				const courseProducts = this.$commonHelper.buildProductList(
					this.getStore,
					this.getFunnels,
					this.$constants.PRODUCT_TYPES.COURSE,
					false,
					true,
					this.user,
					2
				)
				const downloadProducts = this.$commonHelper.buildProductList(
					this.getStore,
					this.getFunnels,
					this.$constants.PRODUCT_TYPES.DIGITAL_DOWNLOAD,
					false,
					true,
					this.user,
					2
				)
				this.availableProducts = []
				communityProducts.forEach(p => {
					this.availableProducts.push({ name: p.label, page_id: p.id, image: p.image })
				})
				courseProducts.forEach(p => {
					this.availableProducts.push({ name: p.label, page_id: p.id, image: p.image })
				})
				downloadProducts.forEach(p => {
					this.availableProducts.push({ name: p.label, page_id: p.id, image: p.image })
				})
			},
			validateForm() {
				let formStatus = true
				const errorObject = {}

				if (!this.selectedProduct) {
					errorObject.productSelectionErrors = 'Please select a product!'
					formStatus = false
				}

				Object.assign(this.formErrors, errorObject)
				return formStatus
			},

			async submitModalForm() {
				this.loading = true
				const status = this.validateForm()
				if (status) {
					try {
						const response = await this.$axios.get('v1/users/fans/bulk/user_fan_ids', {
							params: {
								page: '1',
								timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
								filter_array: JSON.stringify(this.filterArray),
							},
						})
						this.userFanIds = response.data.user_fan_ids
						this.numberOfRowsToProcess = Math.min(this.numberOfRowsToProcess, Math.ceil(this.userFanIds.length / 2))
						this.handleBatch()
						this.$emit('onProductGrant')
					} catch (error) {
						console.error(error)
					}
				} else {
					this.loading = false
				}
			},
			async handleBatch() {
				if (this.processedCount === this.totalCustomers) {
					setTimeout(() => {
						this.hide()
						this.$stanNotify({
							type: 'success', // can be 'info', 'warning', 'success', 'error'
							title: 'Success',
							text: `Product access has been granted.`,
							duration: 10000,
						})
					}, 250)
					return
				}
				const data = {
					page_id: this.selectedProduct.page_id,
					user_fan_ids: this.userFanIds.slice(this.processedCount, this.processedCount + this.numberOfRowsToProcess),
				}
				try {
					await this.$axios.put(`/v1/users/fans/product/bulk`, data)
					this.processedCount = Math.min(this.totalCustomers, this.processedCount + this.numberOfRowsToProcess)
					this.$stanAnalytics(`creator-customers-added-to-product`, {
						meta: { user_id: this.userId, username: this.username },
						props: data,
					})
					this.handleBatch()
				} catch (error) {
					console.error(error)
					Object.assign(this.formErrors, error.response.data)
					this.$stanNotify({
						type: 'error', // can be 'info', 'warning', 'success', 'error'
						title: this.$t('Product Access could not be granted, let us help you with that!'),
						text: error.response.data.message || this.$t('Please email friends@stanwith.me'),
						duration: 4000,
					})
					this.hide()
					this.$emit('close')
				}
			},

			resetForm() {
				this.formErrors = {}
				this.loading = false
				this.processedCount = 0
				this.userFanIds = []
				this.selectedProduct = null
				this.confirm = false
				this.currentPrompt = 'selection'
			},
			goToConfirmPrompt() {
				if (!this.selectedProduct) {
					this.formErrors.productSelectionErrors = 'Please select a product!'
					return
				}
				this.formErrors.productSelectionErrors = ''
				this.confirm = false
				this.currentPrompt = 'confirmation'
			},
			handleCta() {
				this.isCurrentPromptProductSelection ? this.goToConfirmPrompt() : this.submitModalForm()
			},
		},
		watch: {
			getStoreLoading: {
				immediate: true,
				handler(newVal) {
					if (!newVal) {
						this.fetchProductTagList()
					}
				},
			},
		},
	}
</script>

<style lang="scss" scoped>
	.confirmation-card {
		font-family: 'Inter';
		font-style: normal;
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding-top: 20px;
		width: 100%;
		height: 220px;
		background: var(--stan-gray-light-color);
		border-radius: 15px;
		text-align: center;
		margin-bottom: 24px;
		line-height: 100%;

		.confirmation-count {
			font-weight: 700;
			font-size: 80px;
			letter-spacing: -0.02em;
			color: var(--stan-text-dark-color);
		}
		.confirmation-label {
			font-weight: 400;
			font-size: 14px;
			padding-top: 40px;
		}
	}

	.highlight-card {
		background: var(--stan-gray-soft-color);
		border-radius: 12px;
		padding: 14px;
	}
	.customer-count {
		color: var(--stan-primary-primary-color);
	}
	.border {
		border-radius: 8px;
	}

	.product-dropdown::v-deep {
		.menu-wrapper .dropdown {
			&.is-open {
				border-bottom-left-radius: 0px;
				border-bottom-right-radius: 0px;
			}
			padding: 10px;
		}
	}

	.importing-animation {
		padding: 38px;
		display: flex;
		justify-content: center;
		align-items: center;
		svg::v-deep {
			path {
				fill: var(--stan-primary-soft-color);
			}
		}
		svg:first-child {
			width: 78px;
			height: 78px;
			margin: 5px;
			transform: translateX(30px) rotate(8.88deg);
			animation: float1 3s ease-in-out infinite;
		}
		svg:nth-child(2) {
			width: 118px;
			height: 118px;
			animation: float2 4s ease-in-out infinite;
		}
		svg:last-child {
			width: 65px;
			height: 65px;
			margin: 5px;
			transform: translateX(-30px) rotate(-10deg);
			animation: float3 3s ease-in-out infinite;
		}
	}
	@keyframes float1 {
		0% {
			transform: translateX(30px) rotate(8.88deg) translatey(-10px);
		}
		50% {
			transform: translateX(30px) rotate(8.88deg) translatey(0px);
		}
		100% {
			transform: translateX(30px) rotate(8.88deg) translatey(-10px);
		}
	}
	@keyframes float2 {
		0% {
			transform: translatey(0px);
		}
		50% {
			transform: translatey(-20px);
		}
		100% {
			transform: translatey(0px);
		}
	}
	@keyframes float3 {
		0% {
			transform: translateX(-30px) rotate(-10deg) translatey(0px);
		}
		50% {
			transform: translateX(-30px) rotate(-10deg) translatey(-10px);
		}
		100% {
			transform: translateX(-30px) rotate(-10deg) translatey(0px);
		}
	}
</style>
